import { useTranslation } from 'react-i18next';
import { MdOpenInNew } from 'react-icons/md';

import { BROKERS } from '../data/brokers';
import { PROPFIRMS } from '../data/propfirms';
import { computeReliability, type ReliabilityResult } from '../data/reliability';
import type { Broker, Propfirm, ReliabilityBreakdown } from '../data/types';
import { usePropfirmCatalogue } from '../data/use-propfirm-catalogue';
import BrokerDetail from './broker-detail';
import { CountryFlag, FirmLogo, RatingStars, SeniorityBadge } from './comparator-cells';
import { reliabilityColor } from './comparator-utils';
import {
  OfferLink,
  PartnerBadge,
  PartnerCode,
  usePartners,
} from './partner-offer';
import PropfirmDetail from './propfirm-detail';

export type DetailSelection =
  | { kind: 'propfirm'; firm: Propfirm }
  | { kind: 'broker'; broker: Broker };

/** Résout l'entité (propfirm puis broker) à partir du slug d'URL. */
export function findEntityBySlug(
  slug: string,
  firms: readonly Propfirm[] = PROPFIRMS,
): DetailSelection | null {
  const firm = firms.find((f) => f.slug === slug);
  if (firm) return { kind: 'propfirm', firm };
  const broker = BROKERS.find((b) => b.slug === slug);
  if (broker) return { kind: 'broker', broker };
  return null;
}

const RELIABILITY_AXES = [
  { key: 'regulation', label: 'reliability.regulation', max: 40 },
  { key: 'seniority', label: 'reliability.seniority', max: 25 },
  { key: 'reviews', label: 'reliability.reviews', max: 25 },
  { key: 'transparency', label: 'reliability.transparency', max: 10 },
] as const satisfies ReadonlyArray<{ key: keyof ReliabilityBreakdown; label: string; max: number }>;

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function EntityHeader({ entity }: { entity: Propfirm | Broker }) {
  const { t } = useTranslation('propfirms');
  const partner = usePartners().get(entity.slug);
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-white/[0.05] bg-white/[0.02] p-4">
      <div className="flex items-start gap-3">
        <FirmLogo name={entity.name} website={entity.website} logoUrl={entity.logoUrl} size={44} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-base font-semibold text-neutral-100" title={entity.name}>
              {entity.name}
            </h2>
            <CountryFlag headquarters={entity.headquarters} />
          </div>
          <button
            type="button"
            onClick={() => window.api.openExternal(entity.website)}
            title={t('detail.website')}
            className="flex min-w-0 max-w-full items-center gap-1 self-start text-[11px] text-neutral-400 transition-colors hover:text-primary-300 max-md:min-h-11"
          >
            <MdOpenInNew size={12} />
            <span className="min-w-0 truncate">{hostOf(entity.website)}</span>
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <SeniorityBadge foundedYear={entity.foundedYear} />
        <RatingStars score={entity.trustpilotScore} reviews={entity.trustpilotReviews} />
      </div>
      {(partner?.affiliateUrl || partner?.affiliateCode) && (
        <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.05] pt-3">
          <PartnerBadge />
          {partner.affiliateCode && (
            <PartnerCode code={partner.affiliateCode} />
          )}
          <OfferLink website={entity.website} partner={partner} />
        </div>
      )}
    </div>
  );
}

function ReliabilityCard({ result }: { result: ReliabilityResult }) {
  const { t } = useTranslation('propfirms');
  const color = reliabilityColor(result.score);
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-white/[0.05] bg-white/[0.02] p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-500 md:text-[10px]">
          {t('detail.reliabilityTitle')}
        </span>
        <span className="text-xl font-semibold tabular-nums leading-none" style={{ color }}>
          {result.score}
          <span className="text-[11px] font-normal text-neutral-500 md:text-[10px]">/100</span>
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {RELIABILITY_AXES.map((axis) => {
          const value = result.breakdown[axis.key];
          return (
            <div key={axis.key} className="flex flex-col gap-0.5">
              <div className="flex items-center justify-between text-[11px] md:text-[10px]">
                <span className="text-neutral-400">{t(axis.label)}</span>
                <span className="tabular-nums text-neutral-500">
                  {value}
                  <span className="text-neutral-600">/{axis.max}</span>
                </span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-white/[0.06]">
                <div
                  className="h-full rounded-full bg-primary-500"
                  style={{ width: `${Math.min(100, (value / axis.max) * 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DetailView({ slug }: { slug: string }) {
  const { t } = useTranslation('propfirms');
  const { firms } = usePropfirmCatalogue();
  const selection = findEntityBySlug(slug, firms);

  if (!selection) {
    return (
      <div className="flex h-full items-center justify-center text-[12px] text-neutral-500">
        {t('empty')}
      </div>
    );
  }

  const entity = selection.kind === 'propfirm' ? selection.firm : selection.broker;
  const reliability = computeReliability(entity);

  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(300px,360px)_1fr]">
        <div className="flex flex-col gap-4">
          <EntityHeader entity={entity} />
          <ReliabilityCard result={reliability} />
        </div>
        <div className="min-w-0">
          {selection.kind === 'propfirm' ? (
            <PropfirmDetail firm={selection.firm} />
          ) : (
            <BrokerDetail broker={selection.broker} />
          )}
        </div>
      </div>
    </div>
  );
}
